import { useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useTourGuide, TourGuideConfig } from "./useTourGuide";
import { useAutoTourGuide } from "./useAutoTourGuide";
import { useTourGuideContext } from "@root/context/tour-guide-context";
import { updateTourCompletion } from "@store/thunks/userProfile";
import { ROUTE_NAMES, USER_TYPES } from "@root/constants";
import { TourGuideType } from "@root/constants/tour-guide";
import useMediaQ from "@root/hooks/useMediaQ";
import { getLoggedInUser } from "@store/selectors/entities";

type SidebarStepDef = {
  index: number;
  i18nKey: string;
};

const SUBSCRIBER_SIDEBAR_STEPS: SidebarStepDef[] = [
  { index: 0, i18nKey: "sidebarDashboard" },
  { index: 1, i18nKey: "sidebarCourses" },
  { index: 2, i18nKey: "sidebarJournalistApproval" },
  { index: 3, i18nKey: "sidebarJournalist" },
  { index: 4, i18nKey: "sidebarNotices" },
  { index: 5, i18nKey: "sidebarDepartment" },
  { index: 6, i18nKey: "sidebarSubSubscriberMgmt" },
];

const SUBSUBSCRIBER_SIDEBAR_STEPS: SidebarStepDef[] = [
  { index: 0, i18nKey: "sidebarDashboard" },
  { index: 1, i18nKey: "sidebarCourses" },
  { index: 2, i18nKey: "sidebarJournalistApproval" },
  { index: 3, i18nKey: "sidebarJournalist" },
  { index: 4, i18nKey: "sidebarNotices" },
  { index: 5, i18nKey: "sidebarDepartment" },
];

const NON_SUBSCRIBER_SIDEBAR_STEPS: SidebarStepDef[] = [
  { index: 0, i18nKey: "sidebarDashboard" },
  { index: 1, i18nKey: "sidebarCourses" },
  { index: 2, i18nKey: "sidebarJournalist" },
  { index: 3, i18nKey: "sidebarNotices" },
  { index: 4, i18nKey: "sidebarDepartment" },
];

/**
 * Tour Guide Hook for Pledge Dashboard
 *
 * Works for all pledge roles (Subscriber, Sub-subscriber, Non-subscriber).
 * Sidebar steps are built dynamically based on current role.
 */
export const usePledgeDashboardTourGuide = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const user = useSelector(getLoggedInUser) as { role?: number } | null;
  const { setActiveTourModule, setCurrentTourStep } = useTourGuideContext();
  const { sm } = useMediaQ();
  const isMobile = !sm;

  const isOnPledgeDashboard = pathname === ROUTE_NAMES.PLEDGE_DASHBOARD;

  const sidebarSteps: SidebarStepDef[] = useMemo(() => {
    if (user?.role === USER_TYPES.SUBSCRIBER) return SUBSCRIBER_SIDEBAR_STEPS;
    if (user?.role === USER_TYPES.SUBSUBSCRIBER)
      return SUBSUBSCRIBER_SIDEBAR_STEPS;
    if (user?.role === USER_TYPES.NON_SUBSCRIBER)
      return NON_SUBSCRIBER_SIDEBAR_STEPS;
    return SUBSCRIBER_SIDEBAR_STEPS;
  }, [user?.role]);

  const config: TourGuideConfig = useMemo(() => {
    const base = "tourGuide.pledgeDashboard";
    const sidebarStepItems = sidebarSteps.map(({ index, i18nKey }) => ({
      id: `pledge-sidebar-${index}`,
      targetSelector: `[data-tour-item="sidebar-menu-${index}"]`,
      title: t(`${base}.${i18nKey}.title`),
      description: t(`${base}.${i18nKey}.description`),
      position: "right" as const,
      offset: { x: 15, y: 0 },
      highlightPadding: 10,
    }));

    return {
      moduleId: "pledgeDashboard",
      enabled: isOnPledgeDashboard,
      steps: [
        ...sidebarStepItems,
        {
          id: "pledge-sidebar-language",
          targetSelector: '[data-tour-item="sidebar-language-toggle"]',
          title: t(`${base}.sidebarLanguage.title`),
          description: t(`${base}.sidebarLanguage.description`),
          position: "right" as const,
          offset: { x: 15, y: 0 },
          highlightPadding: 10,
        },
        {
          id: "pledge-nav-control",
          targetSelector: '[data-tour-item="journalist-nav-control"]',
          title: t(`${base}.sidebarNavControl.title`),
          description: t(`${base}.sidebarNavControl.description`),
          position: "right" as const,
          offset: { x: 15, y: 0 },
          highlightPadding: 10,
        },
        {
          id: "pledge-header-home",
          targetSelector: '[data-tour-item="header-home-button"]',
          title: t(`${base}.headerHome.title`),
          description: t(`${base}.headerHome.description`),
          position: "bottom" as const,
          offset: { x: 0, y: 15 },
          highlightPadding: 10,
        },
        {
          id: "pledge-stats",
          targetSelector: '[data-tour-item="pledge-dashboard-stats"]',
          title: t(`${base}.stats.title`),
          description: t(`${base}.stats.description`),
          position: "bottom" as const,
          offset: { x: 0, y: 15 },
          highlightPadding: 12,
        },
        {
          id: "pledge-recent-activities",
          targetSelector:
            '[data-tour-item="pledge-dashboard-recent-activities"]',
          title: t(`${base}.recentActivities.title`),
          description: t(`${base}.recentActivities.description`),
          position: "right" as const,
          offset: { x: 15, y: 0 },
          highlightPadding: 12,
        },
        {
          id: "pledge-top-courses",
          targetSelector: '[data-tour-item="pledge-dashboard-top-courses"]',
          title: t(`${base}.topCourses.title`),
          description: t(`${base}.topCourses.description`),
          position: "left" as const,
          offset: { x: -15, y: 0 },
          highlightPadding: 12,
        },
        {
          id: "pledge-journalist-section",
          targetSelector:
            '[data-tour-item="pledge-dashboard-journalist-section"]',
          title: t(`${base}.journalistSection.title`),
          description: t(`${base}.journalistSection.description`),
          position: "top" as const,
          offset: { x: 0, y: -15 },
          highlightPadding: 12,
        },
      ],
    };
  }, [isOnPledgeDashboard, t, sidebarSteps]);

  const baseTourGuide = useTourGuide(config);

  const handleTourComplete = () => {
    dispatch(
      updateTourCompletion({
        payload: {
          tourGuides: [{ type: TourGuideType.PledgeDashboard, isDone: true }],
        },
        onSuccess: () => {},
        onError: () => {},
      }) as any,
    );
  };

  const pledgeDashboardTourGuide = useAutoTourGuide({
    tourGuide: baseTourGuide,
    tourGuideType: TourGuideType.PledgeDashboard,
    isOnTourPath: isOnPledgeDashboard && !isMobile,
    onTourComplete: handleTourComplete,
  });

  useEffect(() => {
    if (pledgeDashboardTourGuide.isActive) {
      setActiveTourModule("pledgeDashboard");
      setCurrentTourStep(pledgeDashboardTourGuide.currentStep);
    } else {
      setActiveTourModule(null);
    }
  }, [
    pledgeDashboardTourGuide.isActive,
    pledgeDashboardTourGuide.currentStep,
    setActiveTourModule,
    setCurrentTourStep,
  ]);

  return {
    pledgeDashboardTourGuide,
    isMobile,
  };
};
